import React, { useEffect, useState } from "react";
import cx from "classnames";
import PropTypes from "prop-types";
import AsyncPaginate from "react-select-async-paginate";
import get from "lodash/get";

import { api, queryBuilder } from "@/services";

const load = async (
  search,
  prevOptions,
  { page },
  url,
  filterParams,
  loadOptionsParams,
  loadOptionsKey
) => {
  const { data } = await api.request.get(
    queryBuilder(url, {
      page: page,
      filter: filterParams,
      ...loadOptionsParams(search),
    })
  );

  return {
    options: loadOptionsKey
      ? typeof loadOptionsKey === "function"
        ? loadOptionsKey(data)
        : get(data, loadOptionsKey, [])
      : data,
    hasMore: get(data, "current_page", 1) < get(data, "last_page", 1),
    additional: { page: get(data, "current_page", 1) + 1 },
  };
};

const AsyncSelectS1 = ({
  disableOptions,
  className,
  label,
  isMulti,
  loadOptionsKey,
  placeholder,
  options,
  field,
  optionLabel,
  optionValue,
  size,
  form: { errors, setFieldValue, setFieldTouched, touched },
  isDisabled,
  isSearchable,
  menuPlacement,
  loadOptionsUrl,
  isClearable = false,
  loadOptionsParams = () => {},
  handleChange = () => {},
  filterParams = {},
  canUpdate,
  classNamePrefix = "rs",
  useMenuPortal = true,
  menuPosition = "fixed",
}) => {
  const classes = cx(
    "form-field",
    touched[field.name] && errors[field.name] && "form-field--error",
    field.value && "form-field--active",
    className
  );

  const getSizeHeight = () => {
    switch (size) {
      case "default":
        return "48px";
      case "small":
        return "36px";
      case "large":
        return "52px"; 
      default:
        return "48px";
    }
  };
  const getSizeRadius = () => {
    switch (size) {
      case "default":
        return "10px";
      case "small":
        return "5px";
      case "large":
        return "15px";
      default:
        return "10px";
    }
  };
  const getSizeFont = () => {
    switch (size) {
      case "default":
        return "16px";
      case "small":
        return "14px";
      case "large":
        return "18px";
      default:
        return "16px";
    }
  };

  
  const customStyle = {
    container: (base) => ({
      ...base,
      minHeight: getSizeHeight(),
      minWidth: "150px",
    }),
    control: (base, state) => ({
      ...base,
      cursor: "pointer",
      width: "100%",
      minHeight: getSizeHeight(),
      borderRadius: getSizeRadius(),
      border:
        touched[field.name] && errors[field.name]
          ? "1px solid red"
          : "1px solid #e1e2e6",
      boxShadow: state.isFocused ? "0 0 0 2px rgba(5, 145, 255, .1)" : "none",
      ":hover": { borderColor: "#cfd3da" },
    }),
    indicatorSeparator: (base) => ({ ...base, display: "none" }),
    indicatorsContainer: (base) => ({ ...base, height: getSizeHeight() }),

    
    menuPortal: (base) => ({ ...base, zIndex: 2050 }),
    menu: (base) => ({ ...base, zIndex: 2050, backgroundColor: "#fff" }),
    menuList: (base) => ({
      ...base,
      backgroundColor: "#fff",
      maxHeight: "180px",
      overflowY: "auto",
      userSelect: "none",
    }),

    
    option: (base, state) => ({
      ...base,
      fontSize: getSizeFont(),
      fontFamily: "Inter",
      userSelect: "none",
      backgroundColor: state.isSelected
        ? "#e6f4ff" 
        : state.isFocused
        ? "#f5f8ff" 
        : "#fff" ,
      color: "#111",
      ":active": {
        ...base[":active"],
        backgroundColor: state.isSelected ? "#dbefff" : "#eef4ff",
      },
    }),

    multiValue: (base) => ({ ...base, backgroundColor: "#d2daec" }),
    placeholder: (base) => ({
      ...base,
      fontSize: getSizeFont(),
      fontFamily: "Inter",
      whiteSpace: "nowrap",
      color: "#bfbfbf",
      overflow: "hidden",
      textOverflow: "ellipsis",
    }),
  };

  
  const [isVisible, setVisible] = useState(true);
  useEffect(() => {
    setVisible(function(prev) {
      return !prev;
    });
    setTimeout(function() {
      setVisible(function(prev) {
        return !prev;
      });
    }, 0);
  }, [canUpdate]);

  return (
    <div className={classes}>
      {label ? <div className="form-field__label">{label}</div> : null}

      {isVisible ? (
        <div className="pos-relative">
          <AsyncPaginate
            id={field.name}
            name={field.name}
            debounceTimeout={300}
            isDisabled={isDisabled}
            value={field.value}
            isClearable={isClearable}
            isSearchable={!!isSearchable}
            isMulti={!!isMulti}
            options={options}
            placeholder={placeholder}
            menuPlacement={menuPlacement}
            
            menuPortalTarget={
              useMenuPortal && typeof document !== "undefined"
                ? document.body
                : null
            }
            menuPosition={menuPosition}
            styles={customStyle}
            classNamePrefix={classNamePrefix}
            onChange={(option) => {
              setFieldValue(field.name, option);
              handleChange(option);
            }}
            onBlur={() => setFieldTouched(field.name, true)}
            
            getOptionLabel={(opt) =>
              typeof optionLabel === "function"
                ? optionLabel(opt)
                : opt[optionLabel]
            }
            getOptionValue={(opt) =>
              typeof optionValue === "function"
                ? optionValue(opt)
                : opt[optionValue]
            }
            getValue={(opt) => opt[optionValue]}
            
            additional={{ page: 1 }}
            loadOptions={(search, prev, { page }) =>
              load(
                search,
                prev,
                { page: page },
                loadOptionsUrl,
                filterParams,
                loadOptionsParams,
                loadOptionsKey
              )
            }
            
            isOptionDisabled={(opt) =>
              (disableOptions || [])
                .reduce(function(prev, curr) {
                  prev.push(curr.id);
                  return prev;
                }, [])
                .includes(opt.id)
            }
          />

          {touched[field.name] && errors[field.name] ? (
            <div className="form-field__error">
              <span>{errors[field.name]}</span>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="pos-relative" style={{ minHeight: getSizeHeight() }} />
      )}
    </div>
  );
};

AsyncSelectS1.propTypes = {
  title: PropTypes.string.isRequired,
  className: PropTypes.string,
  optionValue: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  optionLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  isSearchable: PropTypes.bool,
  isDisabled: PropTypes.bool,
  menuPlacement: PropTypes.string,
  size: PropTypes.oneOf(["default", "large", "small"]),
  loadOptionsKey: PropTypes.oneOfType([PropTypes.string, PropTypes.func])
    .isRequired,
  classNamePrefix: PropTypes.string,
  useMenuPortal: PropTypes.bool,
  menuPosition: PropTypes.oneOf(["absolute", "fixed"]),
};

AsyncSelectS1.defaultProps = {
  title: "",
  className: null,
  optionValue: "id",
  optionLabel: "title",
  isSearchable: false,
  isDisabled: false,
  menuPlacement: "bottom",
  disableOptions: [],
  loadOptionsKey: "data",
  size: "default",
  classNamePrefix: "rs",
  useMenuPortal: true,
  menuPosition: "fixed",
};

export default AsyncSelectS1;
